import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Paintbrush, 
  Car, 
  Wrench, 
  Sparkles, 
  Layers, 
  Shield, 
  CircleDot,
  ArrowRight,
  CheckCircle2,
  Users,
  Building2,
  Truck
} from "lucide-react";

export const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToDetail = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <section
      id="leistungen"
      ref={sectionRef}
      className="py-20 lg:py-28 section-light"
    >
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
            Unsere Leistungen
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Komplettservice für Ihr Fahrzeug
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            Als Ihr Komplettdienstleister für Lackierung und Karosserie bieten wir Ihnen alle 
            Leistungen aus einer Hand – schnell, professionell und fair.
          </p>
          
          {/* Target Groups */}
          <div className="flex flex-wrap justify-center gap-4">
            <span className="text-sm text-muted-foreground mr-2">Für wen?</span>
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-full px-4 py-1.5">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Privatkunden</span>
            </div>
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-full px-4 py-1.5">
              <Truck className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Flottenbetreiber</span>
            </div>
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-full px-4 py-1.5">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Autohäuser</span>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${isVisible ? 'stagger-children' : ''}`}>
          {/* Fahrzeuglackierung */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Paintbrush className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Fahrzeuglackierung
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Professionelle Lackierung mit digitaler Farbtonmessung.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Perfekte Farbabstimmung</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Schnelle Bearbeitung</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#lackierung-detail")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Karosseriebau */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Car className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Karosseriebau
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Richtarbeiten, Ausbeulen und Schweißarbeiten aus Meisterhand.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Modernste Technik</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Faire Preise</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#karosseriebau-detail")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Unfallinstandsetzung */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Wrench className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Unfallinstandsetzung
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Komplette Unfallschadenabwicklung von der Analyse bis zur Reparatur.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Versicherungsservice inkl.</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Weniger Standzeit</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#unfall-detail")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Spot-Repair */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Sparkles className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Spot-Repair
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Schnelle Beseitigung von Kratzern, Dellen und Parkremplern.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Ideal vor Leasingrückgabe</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Kostengünstig</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#spot-repair-detail")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Kunststoffreparatur */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Layers className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Kunststoffreparatur
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Fachgerechte Reparatur von Stoßfängern und Verkleidungen.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Originalgetreue Reparatur</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Nachhaltig</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#zusatzleistungen")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Glasreparatur */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Shield className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Glasreparatur
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Steinschlagreparatur und Scheibentausch für alle Fahrzeuge.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Schneller Service</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Direktabrechnung</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#zusatzleistungen")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Felgenaufbereitung */}
          <Card className="group card-hover bg-card border border-border/50 hover:border-primary/30 flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <CircleDot className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
              </div>
              <CardTitle className="font-display text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-300">
                Felgenaufbereitung
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-grow pt-0">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Aufbereitung und Instandsetzung von Leichtmetallfelgen.
              </p>
              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Wie neu</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Werterhalt</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => scrollToDetail(e, "#zusatzleistungen")} className="w-fit p-0 h-auto text-primary hover:text-primary-dark hover:bg-transparent gap-1 group/btn mt-auto">
                Mehr erfahren <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Services;
